import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAmountDown, faSortAmountUp } from '@fortawesome/free-solid-svg-icons';
import CommentEntry from './CommentEntry';
import Dialog from '../dialog/dialog';
import ModalDialog from '../modal_dialog';
import { commentsRequest, commentScopeKey, commentErrorKey } from '../../utils/comments-api';
import { buildCommentTimeline, commentGroupDate } from '../../utils/comment-timeline';
import { lineCommentScopes } from '../../utils/comment-display';
import './comments.scss';

const sections = ['invoice', 'source', 'lines', 'removed'];
const uniqueEntries = (entries) => [...new Map(entries.map((entry) => [entry.id, entry])).values()]
  .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '') || a.id.localeCompare(b.id));
const scopeLabel = (scope, t, scopes) => {
  if (scope.section === 'invoice') return t.read(scope.subject.type === 'expense' ? 'comment_ui.expense' : 'comment_ui.invoice');
  if (scope.section === 'removed') return t.read('comment_ui.removed_line');
  if (scope.section === 'source') {
    const numbers = scopes.filter((item) => item.section === 'lines' && scope.invoice_line_ids?.includes(item.subject.line_id)).map((item) => item.number);
    const label = t.read(scope.subject.line_id ? 'comment_ui.expense_allocation' : 'comment_ui.expense');
    return numbers.length ? `${label} · ${t.read('comment_ui.invoice_lines')} ${numbers.join(', ')}` : label;
  }
  return `${scope.shared_expense ? `${t.read('comment_ui.expense')} · ` : ''}${t.read('comment_ui.line')} ${(scope.numbers || [scope.number]).join(', ')}${scope.description ? ` · ${scope.description}` : ''}`;
};
// Header destinations need the expense ID: several shared groups have no line ID.
const destinationKey = (scope) => scope.subject.type === 'expense' && !scope.subject.line_id
  ? commentScopeKey(scope) : scope.subject.line_id || 'invoice';
const readScope = (scope, cursor) => commentsRequest(`/comments/${scope.subject.type}/${scope.subject.id}`, 'GET', {
  ...scope.query, ...(scope.subject.line_id ? { line_id: scope.subject.line_id } : {}), limit: 50, order: 'newest',
  ...(cursor ? { cursor } : {}),
});

const CommentsPanel = ({ invoiceId, subjectType = 'invoice', lineId = null, refreshKey, data, legacyText = '', onDraftChange, readOnly = false }) => {
  const t = data.t;
  const identity = `${subjectType}:${invoiceId}:${lineId ?? 'all'}`;
  const [view, setView] = useState({ invoiceId: null, scopes: [], pages: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState('invoice');
  // One draft follows the composer between the top and an authorized group.
  const [inlineScopeKey, setInlineScopeKey] = useState(null);
  const composerInput = useRef(null);
  const previousInlineScopeKey = useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editBody, setEditBody] = useState('');
  const editRequest = useRef(null);
  const [tab, setTab] = useState(lineId === null ? 'all' : 'comments');
  // One local direction applies to all tabs without resetting the draft or feed.
  const [descending, setDescending] = useState(true);
  const activity = tab === 'activity';
  const generation = useRef(0);
  const requestIdentity = useRef(null);
  const draftListener = useRef(onDraftChange);
  draftListener.current = onDraftChange;
  const current = view.invoiceId === identity;
  const scopes = current ? view.scopes : [];
  const pages = current ? view.pages : {};
  const writable = readOnly ? [] : scopes.filter((scope) => scope.can_add && pages[commentScopeKey(scope)]?.scope?.capabilities?.can_add);
  const inlineScope = !activity && writable.find((scope) => commentScopeKey(scope) === inlineScopeKey);
  useEffect(() => {
    // Move keyboard focus with the draft, without stealing focus on initial load.
    if (inlineScopeKey || previousInlineScopeKey.current) composerInput.current?.focus();
    previousInlineScopeKey.current = inlineScopeKey;
  }, [inlineScopeKey]);
  const hasDestination = writable.some((scope) => destinationKey(scope) === selected);

  useEffect(() => { draftListener.current?.(Boolean(body.trim()) || Boolean(editTarget) || busy); }, [body, editTarget, busy]);
  useEffect(() => () => draftListener.current?.(false), []);

  const fail = (caught, token) => {
    if (token !== generation.current) return;
    // Clear cached content on any failed authorization/context refresh. The draft
    // is retained locally, but stale destinations cannot be used to post again.
    setView({ invoiceId: identity, scopes: [], pages: {} });
    setConfirm(null);
    setInlineScopeKey(null);
    setError(commentErrorKey(caught));
  };
  const reload = async () => {
    const token = ++generation.current;
    setLoading(true);
    // Refreshes may remove a group; keep its draft and destination at the top.
    setInlineScopeKey(null);
    setError('');
    setView({ invoiceId: identity, scopes: [], pages: {} });
    try {
      const result = await commentsRequest(`/comments/${subjectType}/${invoiceId}/scopes`);
      if (token !== generation.current) return;
      const visibleScopes = lineCommentScopes(result.scopes, lineId);
      const loaded = await Promise.all(visibleScopes.map(async (scope) => [commentScopeKey(scope), await readScope(scope)]));
      if (token !== generation.current) return;
      setView({ invoiceId: identity, scopes: visibleScopes, pages: Object.fromEntries(loaded) });
      const available = visibleScopes.filter((scope) => scope.can_add);
      if (!body.trim()) setSelected((previous) => available.some((scope) => destinationKey(scope) === previous)
        ? previous : (available[0] ? destinationKey(available[0]) : 'invoice'));
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setLoading(false); }
  };
  useEffect(() => {
    // Invoice or line navigation must not show the previous context's draft.
    setBody('');
    requestIdentity.current = null;
    setBusy(false);
    setConfirm(null);
    setEditTarget(null);
    setEditBody('');
    // Changing the modal's line starts a new discussion context and destination.
    setTab(lineId === null ? 'all' : 'comments');
    reload();
    return () => { generation.current += 1; };
    // Context navigation reloads the feed; successful saves refresh the feed separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, subjectType, lineId]);
  const previousRefresh = useRef(refreshKey);
  useEffect(() => {
    if (previousRefresh.current !== refreshKey) { previousRefresh.current = refreshKey; reload(); }
    // Keep an unsent draft during ordinary invoice saves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const post = async (event) => {
    event.preventDefault();
    const scope = writable.find((item) => destinationKey(item) === selected);
    if (!scope || !body.trim() || busy) return;
    const digest = JSON.stringify([subjectType, invoiceId, selected, body]);
    if (requestIdentity.current?.digest !== digest) {
      requestIdentity.current = { digest, key: window.crypto.randomUUID() };
    }
    const token = generation.current;
    setBusy(true);
    setError('');
    try {
      const entry = await commentsRequest(`/comments/${scope.subject.type}/${scope.subject.id}`, 'POST', {
        ...scope.query, body, client_request_id: requestIdentity.current.key, ...(scope.subject.line_id ? { line_id: scope.subject.line_id } : {}),
      });
      if (token !== generation.current) return;
      const key = commentScopeKey(scope);
      setView((previous) => ({ ...previous, pages: { ...previous.pages,
        [key]: { ...previous.pages[key], entries: uniqueEntries([...previous.pages[key].entries, entry]) } } }));
      setBody('');
      setInlineScopeKey(null);
      setTab((currentTab) => currentTab === 'activity' ? 'all' : currentTab);
      requestIdentity.current = null;
    } catch (caught) {
      if (token !== generation.current) return;
      if (![403, 404, 409].includes(caught?.response?.status)) setError(commentErrorKey(caught));
      else fail(caught, token);
    } finally { if (token === generation.current) setBusy(false); }
  };
  const loadMore = async (scope) => {
    const token = generation.current;
    const key = commentScopeKey(scope);
    setBusy(true);
    try {
      const page = await readScope(scope, pages[key].next_cursor);
      if (token !== generation.current) return;
      setView((previous) => ({ ...previous, pages: { ...previous.pages,
        [key]: { ...page, entries: uniqueEntries([...previous.pages[key].entries, ...page.entries]) } } }));
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const remove = async () => {
    if (!confirm || busy) return;
    const token = generation.current;
    setBusy(true);
    try {
      const deleted = await commentsRequest(`/comments/${confirm.entry.id}`, 'DELETE', {
        ...confirm.scope.query, expected_version: confirm.entry.version });
      if (token !== generation.current) return;
      setView((previous) => ({ ...previous, pages: Object.fromEntries(Object.entries(previous.pages).map(([key, page]) =>
        [key, { ...page, entries: page.entries.map((entry) => entry.id === deleted.id ? deleted : entry) }])) }));
      setConfirm(null);
    } catch (caught) { fail(caught, token); }
    finally { if (token === generation.current) setBusy(false); }
  };
  const beginEdit = (entry, scope) => {
    setError('');
    setEditTarget({ entry, scope });
    setEditBody(entry.body || '');
    editRequest.current = null;
  };
  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editTarget || busy || !editBody.trim()) return;
    const token = generation.current;
    // Retrying an uncertain request reuses its key; changed text starts a new edit.
    if (!editRequest.current || editRequest.current.body !== editBody) {
      editRequest.current = { body: editBody, id: crypto.randomUUID() };
    }
    setBusy(true);
    setError('');
    try {
      const updated = await commentsRequest(`/comments/${editTarget.entry.id}`, 'PATCH', {
        ...editTarget.scope.query, body: editBody, expected_version: editTarget.entry.version,
        client_request_id: editRequest.current.id });
      if (token !== generation.current) return;
      setView((previous) => ({ ...previous, pages: Object.fromEntries(Object.entries(previous.pages).map(([key, page]) =>
        [key, { ...page, entries: page.entries.map((entry) => entry.id === updated.id ? updated : entry) }])) }));
      setEditTarget(null);
    } catch (caught) {
      // Keep the edit draft on outages; clear stale access/context data on rejections.
      if ([403, 404, 409].includes(caught?.response?.status)) fail(caught, token);
      else if (token === generation.current) setError(commentErrorKey(caught));
    } finally { if (token === generation.current) setBusy(false); }
  };
  const composer = !readOnly && <form className={`comment-composer${inlineScope ? ' comment-composer-inline' : ''}`} onSubmit={post}
        aria-label={inlineScope ? t.read('comment_ui.composer_for').replace('{scope}', scopeLabel(inlineScope, t, scopes)) : t.read('comments')}>
        <span className="comment-avatar" aria-hidden="true">{[data.firstName, data.lastName].filter(Boolean).map((name) => name[0]).join('')}</span>
        <div className="comment-content"><textarea ref={composerInput} className="form-control form-control-sm" aria-label={t.read('comment_ui.add_label')} placeholder={t.read('comment_ui.placeholder')}
          maxLength={2000} value={body} disabled={busy || loading} onChange={(event) => setBody(event.target.value)} />
          <div className="comment-post-controls">
            {/* Reserve the destination row while permissions load, without visible loading text. */}
            {loading ? <div className="comment-destination-loading" role="status" aria-label={t.read('comment_ui.loading')}>
              <span className="skeleton comment-destination-placeholder" aria-hidden="true" />
            </div> : inlineScope ? <span>{t.read('comment_ui.destination')} <strong>{scopeLabel(inlineScope, t, scopes)}</strong></span> : writable.length > 0 && <label>{t.read('comment_ui.destination')} <select className="form-control form-control-sm" aria-label={t.read('comment_ui.destination')}
              value={selected} disabled={busy} onChange={(event) => setSelected(event.target.value)}>
              {!hasDestination && <option value={selected} disabled>{t.read('comment_ui.destination_unavailable')}</option>}
              {writable.map((scope) => <option key={commentScopeKey(scope)} value={destinationKey(scope)}>{scopeLabel(scope, t, scopes)}</option>)}
            </select></label>}
            {/* An unavailable feed does not establish read-only permissions. */}
            {!loading && !error && !writable.length && <small>{t.read('comment_ui.read_only')}</small>}
            {inlineScope && <button type="button" className="comment-action btn btn-link btn-sm" disabled={busy}
              onClick={() => setInlineScopeKey(null)}>{t.read('cancel')}</button>}
            <button className="btn btn-sm btn-primary" type="submit" disabled={!hasDestination || !body.trim() || busy || loading}>{t.read('comment_ui.add')}</button>
          </div>
        </div>
      </form>;
  // Both views share the discussion actions and one inline draft.
  const renderDiscussion = (group, label = false) => {
    const { scope, key, page, entries } = group;
    const section = scope.section;
    const canReply = !activity && !readOnly && section !== 'source' && section !== 'removed'
      && writable.some((item) => commentScopeKey(item) === key);
    const replying = inlineScope && inlineScopeKey === key;
    return <div key={key} className="comment-discussion" role="group" aria-label={scopeLabel(scope, t, scopes)}>
      {(label || section !== 'invoice') && <div className="comment-scope-label">{scopeLabel(scope, t, scopes)}</div>}
      <div className={`comment-discussion-entries${canReply && !replying ? ' comment-discussion-entries--reply' : ''}`}>
        {entries.map((entry) => <CommentEntry key={entry.id} entry={entry} data={data} readOnly={readOnly || section === 'source'} deleting={busy}
          onDelete={(target) => setConfirm({ entry: target, scope })} onEdit={(target) => beginEdit(target, scope)} />)}
        {page?.next_cursor && <button type="button" className="comment-discussion-more comment-action btn btn-link btn-sm" disabled={busy} onClick={() => loadMore(scope)}>{t.read('comment_ui.load_more')}</button>}
        {canReply && !replying && <div className="comment-discussion-reply"><button type="button" className="comment-action btn btn-link btn-sm" disabled={busy || loading}
          onClick={() => { setSelected(destinationKey(scope)); setInlineScopeKey(key); }}>{t.read('comment_ui.comment')}</button></div>}
      </div>
      {canReply && replying && composer}
    </div>;
  };
  const groups = scopes.map((scope) => {
    const key = commentScopeKey(scope);
    const page = pages[key];
    const entries = uniqueEntries(page?.entries || []);
    const history = lineId === null && scope.section === 'invoice'
      ? entries.filter((entry) => entry.kind === 'legacy_import') : [];
    // Import time must not position the current discussion in the timeline.
    const discussionPage = history.some((entry) => entry.created_at === page?.scope?.latest_comment_at)
      ? { ...page, scope: { ...page.scope, latest_comment_at: null } } : page;
    return { scope, key, page: discussionPage, history,
      entries: entries.filter((entry) => entry.kind !== 'system_note' && !history.includes(entry)) };
  });
  const history = uniqueEntries(groups.flatMap((group) => group.history));
  const historicalBodies = history.filter((entry) => !entry.deleted && entry.body?.trim()).map((entry) => entry.body);
  if (!history.length && legacyText?.trim() && lineId === null && scopes.some((scope) => scope.section === 'invoice')) {
    historicalBodies.push(legacyText);
  }
  const timeline = buildCommentTimeline(groups, { activitiesOnly: activity, inlineScopeKey, descending });
  const showTimeline = timeline.length || (activity && groups.some((group) => group.page?.next_cursor));
  return (
    <div className="xpens-comments">
      <div className="comments-heading"><h4>{t.read('comments')}</h4>
        <button type="button" className="comment-sort btn btn-link btn-sm text-secondary"
          title={t.read('comment_ui.reverse_sort')} aria-label={t.read('comment_ui.reverse_sort')} aria-pressed={descending}
          onClick={() => setDescending((current) => !current)}>
          <FontAwesomeIcon icon={descending ? faSortAmountUp : faSortAmountDown} />
        </button></div>
      <div className="comments-tabs">{[
        ['all', t.read('comment_ui.all')], ['comments', t.read('comments')], ['activity', t.read('comment_ui.activity')],
      ].map(([value, label]) => <button key={value} type="button" className={`btn btn-sm ${tab === value ? 'text-primary' : 'text-secondary'}`}
        aria-pressed={tab === value} onClick={() => { setTab(value); setInlineScopeKey(null); }}>{label}</button>)}</div>
      {error && !editTarget && <div role="alert"><p>{t.read(error)}</p><button type="button" className="comment-action btn btn-link btn-sm" disabled={busy || loading} onClick={reload}>{t.read('comment_ui.refresh')}</button></div>}
      {!inlineScope ? composer : <button type="button" className="comment-composer-trigger btn btn-light text-muted text-left" disabled={busy}
        onClick={() => setInlineScopeKey(null)}>{t.read('comment_ui.placeholder')}</button>}
      {editTarget && <Dialog isOpen onClose={() => { if (!busy) setEditTarget(null); }} closeDisabled={busy}
        closeLabel={t.read('comment_ui.close')} labelledBy={`comment-edit-${subjectType}-${invoiceId}`} additionnalStyles="comment-edit-dialog">
        <form onSubmit={saveEdit}>
          <h4 id={`comment-edit-${subjectType}-${invoiceId}`}>{t.read('comment_ui.edit_comment')}</h4>
          {error && <p role="alert">{t.read(error)}</p>}
          <textarea className="form-control" aria-label={t.read('comment_ui.edit_comment')} value={editBody}
            autoFocus maxLength={2000} disabled={busy} onChange={(event) => setEditBody(event.target.value)} />
          <div className="mt-3">
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !editBody.trim() || error === 'error_comments_conflict' || error === 'error_comments_access'}>{t.read('comment_ui.save')}</button>
            <button type="button" className="comment-action btn btn-link btn-sm" disabled={busy} onClick={() => setEditTarget(null)}>{t.read('cancel')}</button>
          </div>
        </form>
      </Dialog>}
      {confirm && <ModalDialog data={data} title={t.read('comment_ui.confirm_delete')}
        variant="confirm" size="sm" dividers zIndex={1060} closeDisabled={busy}
        cancelAction={() => setConfirm(null)} cancelText={t.read('cancel')}
        onClose={() => setConfirm(null)} okAction={remove} okText={t.read('comment_ui.confirm_delete')}
        destructive okLoading={busy} invalid={busy} finishProcessing={busy}>
        <p className="mb-0">{t.read('comment_ui.delete_question')}</p>
      </ModalDialog>}
      {tab !== 'comments' ? (showTimeline ? <section aria-label={t.read(activity ? 'comment_ui.activity' : 'comment_ui.all')} className="comment-section comment-timeline">
        {timeline.map((item) => item.type === 'discussion' ? renderDiscussion(item.group, true)
          : <div key={item.key} className="comment-timeline-activity">
            <small>{scopeLabel(item.scope, t, scopes)}</small>
            <CommentEntry entry={item.entry} data={data} readOnly={readOnly || item.scope.section === 'source'} deleting={busy}
              onDelete={(target) => setConfirm({ entry: target, scope: item.scope })} onEdit={(target) => beginEdit(target, item.scope)} />
          </div>)}
        {activity && groups.filter((group) => group.page?.next_cursor).map((group) => <div key={group.key}>
          <button type="button" className="comment-action btn btn-link btn-sm" disabled={busy} onClick={() => loadMore(group.scope)}>
            {t.read('comment_ui.load_more')} · {scopeLabel(group.scope, t, scopes)}</button>
        </div>)}
      </section> : null) : sections.map((section) => {
        const sectionGroups = groups.filter((group) => group.scope.section === section
          && (group.entries.length || group.page?.next_cursor || (inlineScope && group.key === inlineScopeKey)))
          .sort((a, b) => (descending ? -1 : 1) * (commentGroupDate(a) - commentGroupDate(b) || a.key.localeCompare(b.key)));
        if (!sectionGroups.length) return null;
        const heading = `comment_ui.${section === 'invoice' && subjectType === 'expense' ? 'expense' : section}_heading`;
        return <section key={section} aria-label={t.read(heading)} className="comment-section">
          <h5>{t.read(heading)}</h5>
          {sectionGroups.map((group) => renderDiscussion(descending ? { ...group, entries: [...group.entries].reverse() } : group))}
        </section>;
      })}
      {historicalBodies.length > 0 && <div className="comment-legacy">
        <strong>{t.read('comment_ui.previous')}</strong>
        {historicalBodies.map((text, index) => <p key={index} className="comment-body">{text}</p>)}
      </div>}
    </div>
  );
};
export default CommentsPanel;
